import { StatusOffer } from '../enums/status-offer.enum'
import { Comment } from './comment'
import { ConstantKeyValue, ConstantLite } from './constant'
import { Offer } from './offer'
import { Sample } from './sample'
import { Scouting } from './scouting'

export abstract class BaseModel {}

export class HarvestTeam {
  public id: number
  public region: string | null
  public name: string | null | undefined
  public harvester: ConstantKeyValue | null
  public cadence: number | null | undefined
  public speed: number | null | undefined
  public active: boolean | undefined
  public previous_offer?: Offer
  public offers?: Offer[]
  public comments?: Comment[]
  public cropTypes?: {
    id: number
    name: string
  }[]
  public processType?: string | null
  public cadenceUnit?: string | null
  public speedUnit?: string | null
  public regionId?: number | null

  public factoryId?: number | null
  public factoryName: string | null | undefined

  public phoneNumber?: string | null

  public factory?: {
    id: number
    name: string | undefined
    active: boolean
  } | null

  constructor(harvestTeam?: HarvestTeam) {
    this.id = harvestTeam?.id ?? 0
    this.region = harvestTeam?.region ?? null
    this.name = harvestTeam?.name ?? null
    this.harvester = harvestTeam?.harvester ?? null
    this.cropTypes = harvestTeam?.cropTypes ?? []
    this.processType = harvestTeam?.processType ?? null
    this.factoryId = harvestTeam?.factoryId ?? null
    this.factoryName = harvestTeam?.factoryName ?? null
    this.cadence = harvestTeam?.cadence ?? null
    this.cadenceUnit = harvestTeam?.cadenceUnit ?? null
    this.speed = harvestTeam?.speed ?? null
    this.speedUnit = harvestTeam?.speedUnit ?? null
    this.regionId = harvestTeam?.regionId ?? null
    this.active = harvestTeam?.active ?? false
    this.phoneNumber = harvestTeam?.phoneNumber ?? null
    this.offers = [new Offer()]
    this.comments = [new Comment()]
    this.previous_offer = new Offer()
    this.factory = harvestTeam?.factory ?? null
  }
}

export class SaveHarvestOffer {
  id?: number | null
  cropId: number | null
  readyToHarvest: number
  priority: number | null | undefined
  typeOfCulture?: string | null
  quality?: string | null
  risk?: string
  phyto?: string | null
  phytoFilterDate?: string | null
  observation?: string | null
  forecastHarvestDate?: string | null
  areaToHarvest?: number | null
  areaUnit?: string | null
  yield?: number | null
  quantityToHarvest?: number | null
  quantityUnit?: string | null
  bypass: number
  demandSampleDate?: string
  status?: number
  groupCode?: string
  yieldUnit?: string | null
  confirmedHarvestDate?: string | null
  lastCheckOutSiteDate?: string | null
  endDate?: string | Date | null
  durationLots?: number | null

  constructor(offer: Offer) {
    this.id = offer?.id
    this.cropId = offer?.cropId
    this.readyToHarvest = offer?.readyToHarvest ? 1 : 0
    this.priority = offer?.priority
    this.typeOfCulture = offer?.typeOfCulture
    this.quality = offer?.quality
    this.phyto = offer?.phyto
    this.phytoFilterDate = offer?.phytoFilterDate
    this.observation = offer.observation
    this.forecastHarvestDate = offer?.forecastHarvestDate
    this.areaToHarvest = offer?.areaToHarvest
    this.areaUnit = offer?.areaUnit
    this.yield = offer?.yield
    this.quantityToHarvest = offer?.quantityToHarvest
    this.quantityUnit = offer?.quantityUnit
    this.bypass = offer?.bypass ? 1 : 0
    this.status = offer?.status >= 0 ? offer.status : StatusOffer.offerCreated
    this.groupCode = offer?.groupCode ?? 'default'
    this.yieldUnit = offer?.yieldUnit
    this.confirmedHarvestDate = offer?.confirmedHarvestDate
    this.lastCheckOutSiteDate = offer?.lastCheckOutSiteDate
    this.endDate = offer?.endDate
    this.durationLots = offer?.durationLots
  }
}

export class HarvestOfferPriorisation extends Offer {
  declare public samples: Sample[]
  public lastScouting!: Scouting
}

export class HarvestGroupementKey {
  code: string | undefined
  cropTypes: any[] | undefined
  regions?: (string | undefined)[]
  translation: string | undefined
  type: string | undefined

  constructor(constantLite: ConstantLite) {
    this.code = constantLite.code
    this.cropTypes = constantLite.cropTypes
    this.regions = constantLite?.regions?.map((v) => v.name) ?? []
    this.translation = constantLite.translation
    this.type = constantLite.type
  }
}

export class HarvestGroupementType {
  code: string | undefined
  translation: string | undefined

  constructor(harvestGroupementType: ConstantLite) {
    this.code = harvestGroupementType.code
    this.translation = harvestGroupementType.translation
  }
}

export class HarvestDirection {
  id?: number | null
  location_latitude_a: string | null
  location_longitude_a: string | null
  location_latitude_b: string | null
  location_longitude_b: string | null
  distance: number
  motorway: boolean
  region: string | null

  constructor(distance: HarvestDirection) {
    this.id = distance.id ? distance.id : null
    this.location_latitude_a = distance.location_latitude_a ? distance.location_latitude_a : null
    this.location_longitude_a = distance.location_longitude_a ? distance.location_longitude_a : null
    this.location_latitude_b = distance.location_latitude_b ? distance.location_latitude_b : null
    this.location_longitude_b = distance.location_longitude_b ? distance.location_longitude_b : null
    this.distance = distance.distance ? distance.distance : 0.0
    this.motorway = distance.motorway ? distance.motorway : false
    this.region = distance.region ? distance.region : null
  }
}

export class HarvestTeamDAO {
  id?: number
  name: string
  harvester: string
  processType: string
  cadence: number
  cadenceUnit: string
  speed: number
  speedUnit: string
  active: number
  factoryId: number
  regionId: number

  constructor(harvestTeam: HarvestTeamDAO) {
    if (harvestTeam?.id) {
      this.id = harvestTeam?.id ?? null
    }
    this.name = harvestTeam?.name ?? null
    this.harvester = harvestTeam?.harvester ?? null
    this.processType = harvestTeam?.processType ?? null
    this.cadence = harvestTeam?.cadence ?? null
    this.cadenceUnit = harvestTeam?.cadenceUnit ?? null
    this.speed = harvestTeam?.speed ?? null
    this.speedUnit = harvestTeam?.speedUnit ?? null
    this.active = harvestTeam?.active ?? null
    this.factoryId = harvestTeam?.factoryId ?? null
    this.regionId = harvestTeam?.regionId ?? null
  }
}

export class HarvestTeamCropDAO {
  id?: number
  harvestTeamId: number
  cropTypeId: number

  constructor(harvestTeamCrop: HarvestTeamCropDAO) {
    if (harvestTeamCrop?.id) {
      this.id = harvestTeamCrop?.id ?? null
    }
    this.cropTypeId = harvestTeamCrop?.cropTypeId ?? null
    this.harvestTeamId = harvestTeamCrop?.harvestTeamId ?? null
  }
}

export class HarvestTeamPlanification extends HarvestTeam {
  declare offers: Offer[]
  declare comments: Comment[]

  constructor(harvestTeamPlanification: HarvestTeamPlanification) {
    super(harvestTeamPlanification)
    this.offers = harvestTeamPlanification.offers ?? []
    this.comments = harvestTeamPlanification.comments ?? []
  }
}
